import {useContext, useEffect, useState} from "react";
import {observer} from "mobx-react";
import {toJS} from "mobx";

import ErrorBlock from "../ErrorBlock";
import FileBlock from "./FileBlock";
import LoadingBlock from "../LoadingBlock";
import LinkToExternalItem from "./LinkToExternalItem";
import NormalizedFileBlock, {ViewerTypeEnum} from "./NormalizedFileBlock";
import NormalizedFolderBlock from "./NormalizedFolderBlock";
import OneDriveBlock from "./OneDriveBlock";
import {StoreContext} from "../../../../stores/StoreLoader";
import {
    getContentItemIcon,
    getFolderIcon, getLegacyFileIcon,
} from "../../TerracedGridTheme/FilesFoldersBlock/fileUtilities";
import moment from 'moment';
import {IBlockComponentProps} from "../_Block";

export interface ISharedFilesBlockProps extends IBlockComponentProps {
    blockObj: ISharedFilesBlockObj
}

function normalizeLegacySharedFileBlock(blockObj) {
    const blockToReturn = {...blockObj};
    if (!(blockToReturn.file || blockToReturn.folder)) return blockToReturn;
    if (blockToReturn.folder) {
        blockToReturn.folder.files = blockToReturn.folder.files.map((item) => {
            const time = item.modifiedTimestamp.timezone ?
                moment(item.modifiedTimestamp.date + item.modifiedTimestamp.timezone) :
                moment(item.modifiedTimestamp.date);
            return {
                ...item,
                icon: getLegacyFileIcon(item),
                modifiedTimestamp: time.toISOString(),
            }
        });
    } else {
        blockToReturn.file.icon = getLegacyFileIcon(blockToReturn.file);
    }
    return blockToReturn;
}

function normalizeWatsonSharedFileBlock(blockObj, sourceData) {
    const blockToReturn = {...blockObj};

    const isFileVsFolder = sourceData.type.split('.')[1];
    switch (isFileVsFolder) {
        case 'file':
            const contentItemData = sourceData.items[0];
            blockToReturn.file = {};
            // fileUrl is passed if url_path exists (i.e. hide_from_grid block), otherwise use source_url and change viewLink to replace edit or view with preview.
            // sometimes url_path is defined but fileURL isn't so this gross double check is required to not render homepages on file blocks and sharedfiles pages
            blockToReturn.file.viewLink =  sourceData.source_url.replace(/(edit|view)/, "preview");
            blockToReturn.file.icon = getContentItemIcon(contentItemData);
            blockToReturn.file.type = contentItemData.type.split('.')[-1];
            blockToReturn.file.mimeType = contentItemData?.json_data?.file?.mime_type;
            blockToReturn.file.viewerType = getContentItemFileViewerType(contentItemData);
            // if the viewerType is office365 and the file is NOT a pdf, reset the embeddedViewLink.
            // because we aren't getting the right embed link from PHP-> watson call for some reason.
            const fileType = contentItemData.type.split('.')[2];
            if (contentItemData?.json_data?.file?.view_link.match(/onedrive\.live/)) {
                blockToReturn.file.embeddedViewLink = contentItemData?.json_data?.file?.embedded_view_link;
            } else if (blockToReturn.file.viewerType === "office365" && fileType !== "pdf") {
                blockToReturn.file.embeddedViewLink = `${blockToReturn.file.viewLink as string}&action=embedview&wdSmallView=1&wdStartOn=1`;
            } else blockToReturn.file.embeddedViewLink = contentItemData?.json_data?.file?.embedded_view_link;
            blockToReturn.file.id = contentItemData.third_party_id;
            blockToReturn.fileBlockType = 'normalizedFile';
            break;

        case 'folder':
            if (!blockToReturn.folder) blockToReturn.folder = {};
            blockToReturn.fileBlockType = 'normalizedFolder';
            blockToReturn.folder.viewLink = sourceData.source_url;
            blockToReturn.folder.icon = getFolderIcon(sourceData.type);
            blockToReturn.folder.type = sourceData.type;
            blockToReturn.folder.files = sourceData.items?.sort((a, b) => {
                const sortBy = blockToReturn.blockModel.json_data?.block?.settings?.sortBy;
                if (sortBy === "modified") {
                    return new Date(b.json_data[b.json_data.file ? "file" : "folder"]?.updated_at).getTime() - new Date(a.json_data[a.json_data.file ? "file" : "folder"]?.updated_at).getTime();
                } else {
                    return a.title.localeCompare(b.title);
                }
            })?.map((item) => {
                return {
                    icon: getContentItemIcon(item),
                    id: item.id,
                    title: item.title,
                    viewLink: item?.json_data?.[item.json_data.file ? "file" : "folder"]?.view_link,
                    modifiedTimestamp: item?.json_data?.[item.json_data.file ? "file" : "folder"]?.updated_at,
                }
            });
            break;
    }

    return blockToReturn;
}

export function getContentItemFileViewerType(contentItem): ViewerTypeEnum {
    //viewer_type currently only exists in watson for o365.
    // if its a google drawing, make the viewerType an iframe so it is clickable.
    return contentItem.json_data?.file?.viewer_type ?
        contentItem.json_data?.file?.viewer_type :
        (contentItem.json_data?.file?.mime_type === 'application/vnd.google-apps.drawing' ? "iframe" : "google");
}

const SharedFilesBlock = observer((props: ISharedFilesBlockProps) => {
    const {i18nStore} = useContext(StoreContext);
    const [fileData, setFileData] = useState<ISharedFilesBlockObj | null>(null);
    console.debug('fileData: ', fileData);

    useEffect(() => {
        const blockObj = toJS(props.blockObj);
        const contentSources = props.blockObj?.contentSources;
        if (!props.isBlockPreview) {
            try {
                const fallbackContentSource = contentSources?.find((source) => source.language === i18nStore.orgDefaultLocale);
                const currentContentSource = contentSources?.find((source) => source.language === i18nStore.currentOrgSupportedLocale);
                const contentSourceData = currentContentSource ? currentContentSource : fallbackContentSource;
                // if there is data, use it. Otherwise, just use blockObj stuff from PHP.
                if (contentSourceData) {
                    // this value is 'file' or 'folder'
                    setFileData(normalizeWatsonSharedFileBlock(blockObj, contentSourceData));
                } else {
                    setFileData(normalizeLegacySharedFileBlock(blockObj));
                }
            } catch (error) {
                blockObj.fileBlockType = null;
                blockObj.errorMessage = error;
                console.error(error);
            }
        }
    }, [i18nStore.currentOrgSupportedLocale, props.blockObj.contentSources]);

    useEffect(() => {
        if (props.isBlockPreview) {
            if (props.blockObj.contentSources?.length > 0) {
                const sourceData = props.blockObj?.contentSources?.find(s => s.language === i18nStore.blockEditLanguage);
                if (sourceData) {
                    setFileData(normalizeWatsonSharedFileBlock(props.blockObj, sourceData));
                }
            } else {
                setFileData(normalizeLegacySharedFileBlock(props.blockObj));
            }
        }
    }, [i18nStore.blockEditLanguage, props.blockObj?.contentSources])

    if (fileData) {
        switch (fileData.fileBlockType) {
            case "normalizedFile":
                return <NormalizedFileBlock key={i18nStore.currentOrgSupportedLocale + fileData.id} {...props}
                                            blockObj={fileData}/>;
            case "normalizedFolder":
                return <NormalizedFolderBlock key={i18nStore.currentOrgSupportedLocale + fileData.id} {...props}
                                              blockObj={fileData}/>;
            case "file":
                return <FileBlock key={i18nStore.currentOrgSupportedLocale + fileData.id} {...props}
                                  blockObj={fileData}/>;
            case "linkToExternalItem":
                return <LinkToExternalItem {...props} blockObj={fileData}/>;
            case "onedrive":
                return <OneDriveBlock key={i18nStore.currentOrgSupportedLocale + fileData.id} {...props}
                                      blockObj={fileData}/>;
            default:
                return <ErrorBlock key={i18nStore.currentOrgSupportedLocale + fileData.id}
                                   blockObj={{errorMessage: fileData.errorMessage as string}}/>;
        }
    } else {
        return <LoadingBlock/>;
    }
})

export default SharedFilesBlock;
